<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
    v-if="report != null"
>
    <v-alert
      border="left"
      colored-border
      color="primary"
      elevation="2"
      type="info"
      dismissible
    >
      Order analytics are not real time &mdash; it typically takes a day (24 hours) for orders to contribute to a bulk message's order attribution
   </v-alert>

    <v-row>
       <v-col>
            <div class="total-card">
                <p class="total-value">{{$rc.pretty(report.results_summary.sent_count,'integer')}}</p>
                <p class="total-label">Messages Sent</p>
            </div>
       </v-col>

       <v-col>
            <div class="total-card">
                <p class="total-value">{{$rc.pretty(report.count_opt_outs,'integer')}}</p>
                <p class="total-label">Opt outs within {{opt_out_prox_days}} days of bulk message</p>
            </div>
        </v-col>

       <v-col>
            <div class="total-card">
                <p class="total-value">{{$rc.pretty(report.results_summary.order_value,'millicents_whole_dollars')}}</p>
                <p class="total-label">Orders placed by recipients within {{order_prox_days}} days</p>
            </div>
        </v-col>
    </v-row>


    <div class="header">Order Attribution (by Order Type)</div>

    <v-data-table
        :headers="by_order_types_headers"
        :items="report.by_order_types"
        :items-per-page="1000"
        class="elevation-1"
        :hide-default-footer="true"
        disable-sort
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
        style="margin-bottom: 25px"
    >
        <template v-slot:item.count_order_type="{ item }">
            {{$rc.pretty(item.count_order_type, 'int')}}
        </template>
    
        <template v-slot:item.included_order_value="{ item }">
            {{item.order_type_excluded ? null : $rc.pretty(item.total_value_milli_cents, 'millicents_whole_dollars')}}
        </template>

        <template v-slot:item.excluded_order_value="{ item }">
            {{item.order_type_excluded ? $rc.pretty(item.total_value_milli_cents, 'millicents_whole_dollars') : null}}
        </template>

        <template slot="body.append">
            <tr>
                <th>Total</th>
                <th>{{total_order_count}}</th>
                <th>{{total_included_order_value}}</th> 
                <th>{{total_excluded_order_value}}</th>
            </tr>
        </template>
    
    </v-data-table>

     <div class="header">Order Attribution Settings ({{settings_label}})</div>
     <div
        style="margin-bottom: 15px"
     >
        <ul>
        <li>The bulk message was sent to the customer within the {{order_prox_days}} days before the order was placed</li>
        <li>If the customer was sent multiple bulk messages within the {{order_prox_days}} before the order was placed, only the most recent bulk message sent to the customer before the order receives any order attribution</li>
        
        <li
            v-if="exclude_order_types.length == 0"
        >
            The order may be of any type
        </li>
        <li
            v-else
        >
            The order type <u>was not</u>
        </li>
        <ul
            v-if="exclude_order_types.length != 0"
        >
            <li
            v-for="(item, key) in exclude_order_types"
            :key="'ex_order_type_'+key"
            >
            {{item}}
            </li>
        </ul>
        <li>You can customze the order proximity and the types of orders used to caculate order attriubtion  <a style="text-decoration: underline;" v-on:click="onClickEditSettings">by selecting different order attribution settings</a>.</li>
        <li><b>Note when a customer has received several bulk messages:  </b>An order can only be attributed to a single bulk message.  If a customer has received more than one bulk message within the attribution window, the order is only attributed to the most recent bulk message received.</li>
        </ul> 
    </div>

    <div class="header">Search Individual Orders</div>

    <v-text-field
        autocomplete="off"
        label="Search"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
    />
   

    <v-data-table
        :search="full_text"
        :headers="headers"
        :items="report.orders"
        :items-per-page="100000"
        class="elevation-1"
        :hide-default-footer="true"
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"

        :sort-by.sync="sort_by"
        :sort-desc.sync="sort_desc"
    >
    
        <template v-slot:item.date="{ item }">
            {{$rc.pretty(item.time_create,'date')}}
        </template>

        <template v-slot:item.time="{ item }">
            {{$rc.pretty(item.time_create,'time')}}
        </template>

        <template v-slot:item.total_value_milli_cents="{ item }">
            {{$rc.pretty(item.total_value_milli_cents,'millicents_whole_dollars')}}
        </template>

        <template v-slot:item.proximity_bulk_message="{ item }">
            {{prettyDays(item.proximity_bulk_message)}}
        </template>

        <template v-slot:item.action="{ item }">
           <v-menu 
               bottom 
               offset-y  
           >
           
           <template v-slot:activator="{ on }">
                   
               <v-icon 
                   v-on="on"
               >
                   mdi-dots-vertical-circle-outline
               </v-icon>
                   
           </template>
           
           <v-list>
               <v-list-item
                   v-on:click="onClickViewInCRM(item)"
               >
                   <v-list-item-title>View in CRM...</v-list-item-title>
               </v-list-item>
           
           </v-list>
           
           </v-menu> 
        </template>
    
    </v-data-table>

     <m-order-attribution-settings-dialog
      ref="order-attribution-settings-dialog"
      v-on:update="update"
    />

</v-container>
</m-pageframe>
</template>

<script>
export default {
    data() {
        return {
            report: null,

            headers: [
                { text: 'Date', value: 'date'},
                { text: 'Time', value: 'time'},
                { text: 'First', value: 'customer_first_name'},
                { text: 'Last', value: 'customer_last_name'},
                { text: 'Type', value: 'order_type'},
                { text: 'Value', value: 'total_value_milli_cents'},
                { text: 'Time Bulk Message (Days)', value: 'proximity_bulk_message'},
                { text: 'Action', value: 'action'},
            ],

            by_order_types_headers: [
                { text: 'Type', value: 'order_type'},
                { text: 'Order Count', value: 'count_order_type'},
                { text: 'Included Order Value ($)', value: 'included_order_value'},
                { text: 'Excluded Order Value ($)', value: 'excluded_order_value'},
            ],

            'sort_by': ['total_value_milli_cents'],
            'sort_desc': [true],
            'full_text': null,

            duration_days_opt_out: 7,
            duration_days_order_attrib: 14,
            order_types: [],

        }
    },

    mounted () {
        // By default, select all order types
        this.update();
    },

    methods: {
        update() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/analytics/run-report/one-bulk-message-analytics-group',
            
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'analytics_group_id': this.$route.params.analytics_group_id,
                },
            
                'on_success': (data) => {
                    this.report = data;
                    this.order_types = data.order_attribution_settings.include_order_types;
                },
            
                'on_error': 'Unable to download bulk message analytics',
            });
        },

        prettyDays(value)
        {
            if ( value == null ) return null;
            let ret = Math.floor(value / 24 / 60 / 60 / 1000);

            if ( ret == 0 ) return 'Same Day';

            return ret;
        },

        onClickViewInCRM(item)
        {
            let url = item.url_view_order_in_crm;
            window.open(url,"_blank");
        },

        onClickExportToExcel()
        {

        },

        onClickEditSettings()
        {
            this.$refs['order-attribution-settings-dialog'].showDialog(this.$route.params.analytics_group_id, this.order_attribution_settings_id);
        },
    },

    computed: {
        title() {
            if ( this.report == null )
                return 'Bulk Messange Analytics';

            return this.report.results_summary.analytics_group_label;
        },

        settings_label()
        {
            if ( this.report == null ) return 'RedChirp Default';
            return this.report.results_summary.order_attribution_settings_label;
        },

        order_prox_days()
        {
            if ( this.report == null ) return 14;
            return this.report.results_summary.order_attribution_settings_prox_days;
        },

        exclude_order_types()
        {
            if ( this.report == null ) return [];
            return this.report.results_summary.order_attribution_settings_exclude_order_types;
        },

        opt_out_prox_days()
        {
            if ( this.report == null ) return 7;
            return this.report.results_summary.order_attribution_settings_opt_out_prox_days;
        },

        order_attribution_settings_id()
        {
            if ( this.report == null ) return null;
            return this.report.results_summary.order_attribution_settings_id;
        },

        total_order_count()
        {
            let ret = this.report.by_order_types
                .map((item) => item.count_order_type)
                .reduce((x,y) => x+y,0);

            return this.$rc.pretty(ret, 'int');
        },

        total_included_order_value()
        {
            let ret = this.report.by_order_types
                .map((item) => item.order_type_excluded ? 0 : item.total_value_milli_cents)
                .reduce((x,y) => x+y,0);

            return this.$rc.pretty(ret, 'millicents_whole_dollars');
        },

        total_excluded_order_value()
        {
            let ret = this.report.by_order_types
                .map((item) => item.order_type_excluded ? item.total_value_milli_cents : 0)
                .reduce((x,y) => x+y,0);

            return this.$rc.pretty(ret, 'millicents_whole_dollars');
        },
    },
}
</script>

<style lang="scss" scoped>
.total-card
{
    border: 1px solid rgba(0,0,0,0.15);
    
    padding-top: 30px;
    padding-bottom: 30px;

    padding-left: 15px;
    padding-right: 15px;
    text-align: center;

    margin-bottom: 15px;
}

.total-value
{
    font-weight: 700;
    font-size: 28px;
}

.total-label
{
    margin-bottom: -15px;
}

.header
{
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 15px;
}
</style>